import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreVertical, LogOut, LayoutGrid, Inbox, Pencil, Trash2, ExternalLink, CalendarClock, ArrowUp, ArrowDown } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Wordmark } from "../components/Wordmark";
import { Seo } from "../components/Seo";
import { ImageDropzone, GalleryDropzone } from "../components/ImageDropzone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const EMPTY = { title: "", category: "Residential", location: "", year: "", client: "", about: "", description: "", cover_image: "", gallery: [], featured: false };

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [prefs, setPrefs] = useState([]);
  const [appts, setAppts] = useState([]);
  const [stats, setStats] = useState({ projects: 0, preferences: 0, appointments: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s, pr, ap] = await Promise.all([
        api.get("/projects"),
        api.get("/stats"),
        api.get("/preferences"),
        api.get("/appointments"),
      ]);
      setProjects(p.data);
      setStats(s.data);
      setPrefs(pr.data);
      setAppts(ap.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (ready && !user) navigate("/login");
    if (ready && user) load();
  }, [ready, user, navigate, load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, gallery: p.gallery || [] });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.cover_image) {
      toast.error("Title and cover image are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      gallery: Array.isArray(form.gallery) ? form.gallery : [],
    };
    try {
      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const move = async (id, direction) => {
    try {
      await api.post(`/projects/${id}/move?direction=${direction}`);
      load();
    } catch {
      toast.error("Could not reorder");
    }
  };

  if (!ready || !user) return <div className="min-h-screen flex items-center justify-center text-stone">Loading…</div>;

  return (
    <div className="min-h-screen flex bg-[#F4F3F0]" data-testid="admin-dashboard">
      <Seo title="Studio Dashboard" path="/admin" noIndex />
      {/* Sidebar */}
      <aside className="w-64 bg-ink text-sand hidden md:flex flex-col fixed h-screen">
        <div className="p-6 border-b border-sand/10">
          <Wordmark size="md" invert />
          <p className="text-[10px] uppercase tracking-[0.2em] text-sand/50 mt-3">Studio Admin</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button data-testid="tab-projects" onClick={() => setTab("projects")} className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors ${tab === "projects" ? "bg-sand/10 text-sand" : "text-sand/60 hover:text-sand"}`}>
            <LayoutGrid size={18} /> Projects
          </button>
          <button data-testid="tab-preferences" onClick={() => setTab("preferences")} className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors ${tab === "preferences" ? "bg-sand/10 text-sand" : "text-sand/60 hover:text-sand"}`}>
            <Inbox size={18} /> Quiz Leads
          </button>
          <button data-testid="tab-appointments" onClick={() => setTab("appointments")} className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors ${tab === "appointments" ? "bg-sand/10 text-sand" : "text-sand/60 hover:text-sand"}`}>
            <CalendarClock size={18} /> Appointments
          </button>
        </nav>
        <div className="p-4 border-t border-sand/10">
          <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-sand/60 hover:text-sand transition-colors">
            <LogOut size={18} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 p-6 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-ink">{tab === "projects" ? "Projects" : "Quiz Leads"}</h1>
            <p className="text-sm text-stone mt-1">{user.email}</p>
          </div>
          {tab === "projects" && (
            <Button data-testid="new-project-button" onClick={openNew} className="bg-forest hover:bg-clay text-sand rounded-sm">
              <Plus size={16} className="mr-2" /> New Project
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-ink/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-stone">Projects</p>
            <p className="text-4xl font-serif text-ink mt-2">{stats.projects}</p>
          </div>
          <div className="bg-white border border-ink/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-stone">Quiz Leads</p>
            <p className="text-4xl font-serif text-ink mt-2">{stats.preferences}</p>
          </div>
          <div className="bg-white border border-ink/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-stone">Confirmed Bookings</p>
            <p className="text-4xl font-serif text-ink mt-2">{stats.appointments || 0}</p>
          </div>
          <div className="bg-white border border-ink/10 p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-stone">Booking Revenue</p>
            <p className="text-4xl font-serif text-ink mt-2">₹{((stats.appointments || 0) * 3000).toLocaleString("en-IN")}</p>
          </div>
        </div>

        {tab === "projects" && (
          <div className="bg-white border border-ink/10">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Order</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p, i) => (
                  <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => move(p.id, "up")}
                          disabled={i === 0}
                          data-testid={`project-move-up-${p.id}`}
                          className="w-7 h-7 flex items-center justify-center border border-ink/10 hover:bg-sand-dark disabled:opacity-30"
                          aria-label="Move up"
                        >
                          <ArrowUp size={14} />
                        </button>
                        <button
                          onClick={() => move(p.id, "down")}
                          disabled={i === projects.length - 1}
                          data-testid={`project-move-down-${p.id}`}
                          className="w-7 h-7 flex items-center justify-center border border-ink/10 hover:bg-sand-dark disabled:opacity-30"
                          aria-label="Move down"
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium flex items-center gap-3">
                      <img src={(p.cover_image || "").startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${p.cover_image}` : p.cover_image} alt="" className="w-10 h-10 object-cover" />
                      {p.title}
                    </TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>{p.location}</TableCell>
                    <TableCell>{p.year}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`project-actions-${p.id}`}><MoreVertical size={16} /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(p)}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`/projects/${p.id}`, "_blank")}><ExternalLink size={14} className="mr-2" /> View</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => del(p)} className="text-red-600"><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {tab === "preferences" && (
          <div className="bg-white border border-ink/10">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Style</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Beds</TableHead>
                  <TableHead>Timeline</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {prefs.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-stone py-10">No submissions yet.</TableCell></TableRow>
                )}
                {prefs.map((p) => (
                  <TableRow key={p.id} data-testid={`pref-row-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-sm"><div>{p.email}</div><div className="text-stone">{p.phone}</div></TableCell>
                    <TableCell>{p.style}</TableCell>
                    <TableCell>{p.size}</TableCell>
                    <TableCell>{p.budget}</TableCell>
                    <TableCell>{p.bedrooms}</TableCell>
                    <TableCell>{p.timeline}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        {tab === "appointments" && (
          <div className="bg-white border border-ink/10">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date · Slot (IST)</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appts.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-stone py-10">No appointments yet.</TableCell></TableRow>
                )}
                {appts.map((a) => (
                  <TableRow key={a.id} data-testid={`appt-row-${a.id}`}>
                    <TableCell className="font-medium">
                      <div>{new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                      <div className="text-stone text-sm">{String(a.hour).padStart(2, "0")}:00 – {String(a.hour + 1).padStart(2, "0")}:00</div>
                    </TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell className="text-sm"><div>{a.email}</div><div className="text-stone">{a.phone}</div></TableCell>
                    <TableCell>
                      <span className={`text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-sm ${
                        a.status === "confirmed" ? "bg-forest/10 text-forest" :
                        a.status === "pending" ? "bg-amber-100 text-amber-800" :
                        a.status === "cancelled" ? "bg-stone/20 text-stone" :
                        "bg-red-100 text-red-700"
                      }`}>{a.status}</span>
                    </TableCell>
                    <TableCell>₹{(a.amount_inr || 3000).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-sm text-stone max-w-xs truncate">{a.notes || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      {/* Project dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Title</Label><Input data-testid="form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label>Category</Label><Input data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Location</Label><Input data-testid="form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label>Year</Label><Input data-testid="form-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
              <div><Label>Featured</Label><select data-testid="form-featured" value={form.featured ? "yes" : "no"} onChange={(e) => setForm({ ...form, featured: e.target.value === "yes" })} className="w-full border border-ink/15 px-3 py-2 rounded-sm bg-white"><option value="no">No</option><option value="yes">Yes</option></select></div>
            </div>
            <div><Label>About (short note shown at the top of the project)</Label><Textarea data-testid="form-about" rows={3} value={form.about || ""} onChange={(e) => setForm({ ...form, about: e.target.value })} placeholder="A one- or two-line summary of the project" /></div>
            <div><Label>Cover image</Label><ImageDropzone value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} testId="form-cover-dropzone" /></div>
            <div><Label>Gallery images (drag to reorder)</Label><GalleryDropzone value={form.gallery} onChange={(urls) => setForm({ ...form, gallery: urls })} testId="form-gallery-dropzone" /></div>
            <div><Label>Description</Label><Textarea data-testid="form-description" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-project-button" onClick={save} disabled={saving} className="bg-forest hover:bg-clay text-sand rounded-sm">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
